import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Label,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { TableCard } from '@tempmee/react-blocks/experimental';
import { getCycleStatusSeverity } from '../../../../domain/Awards';
import { ExportLinkGroup } from './ExportLinks';

// Applications and Feedback pair their two links rather than taking a column
// each; ten columns would not fit without horizontal scroll.
const COLUMNS = [
  'Cycle Name',
  // The list is ordered newest-created first, which without this column is an
  // invisible rule.
  'Created',
  'Application Period',
  'End Date',
  'Status',
  'Applications',
  'Feedback',
  'Actions',
];

/**
 * The cycle list, framed by `TableCard`, which owns the loading and empty
 * states so the frame stays put between loads.
 *
 * The error branch is ours: TableCard has no failure state, and "no cycles
 * yet" must not stand in for a request that failed.
 */
export const CycleListTable = ({
  cycles, isPending, isError, totalCount, footer,
}) => {
  // "Add cycle" lives in the page header, so this slot carries the count.
  const count = (
    <Typography variant="body2" className="text-text-secondary">
      {totalCount === 1 ? '1 cycle' : `${totalCount} cycles`}
    </Typography>
  );

  if (isError) {
    return (
      <TableCard title="Cycles">
        <Typography className="text-error">Could not load cycles.</Typography>
      </TableCard>
    );
  }

  return (
    <TableCard
      title="Cycles"
      action={count}
      footer={footer}
      loading={isPending}
      empty={!isPending && cycles.length === 0}
      emptyMessage="No cycles yet."
    >
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {cycles.map((cycle) => (
              <TableRow key={cycle.id} hover>
                <TableCell>{cycle.name}</TableCell>
                <TableCell>{cycle.createdAtLabel}</TableCell>
                <TableCell>{cycle.applicationPeriod}</TableCell>
                <TableCell>{cycle.endDateLabel}</TableCell>
                <TableCell>
                  <Label severity={getCycleStatusSeverity(cycle.status)} variant="standard">
                    {cycle.statusLabel}
                  </Label>
                </TableCell>
                <TableCell><ExportLinkGroup kind="applications" cycleId={cycle.id} /></TableCell>
                <TableCell><ExportLinkGroup kind="feedback" cycleId={cycle.id} /></TableCell>
                <TableCell>
                  {/* Names its destination: "Actions" does not supply the
                      noun the way the old "View" header did. */}
                  <RouterLink to={`/best-in-dental/${cycle.id}`}>View Cycle</RouterLink>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
};

CycleListTable.propTypes = {
  cycles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      applicationPeriod: PropTypes.string.isRequired,
      endDateLabel: PropTypes.string.isRequired,
      createdAtLabel: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      statusLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  /** Server-side total for the active page set, shown in the header. */
  totalCount: PropTypes.number.isRequired,
  /** Footer slot — the page passes its pagination. */
  footer: PropTypes.node,
};

CycleListTable.defaultProps = { footer: null };
